import { QueryClient } from 'react-query';
import { isEmpty, isPlainObject, snakeCase } from 'lodash';
import { join } from 'path';
import { deepMapKeys } from '@appfolio/js-data-mapper';

export const envRoot = (): string =>
  (process.env.NODE_ENV === 'development' ? '/' : '/connect');

export const withEnvRoot = (link: string): string => {
  const root = envRoot();
  return link.startsWith(root) ? link : join(root, link);
};

export const isAbsoluteURL = (link: string): boolean => {
  return !!link.match(/^(http[s]?:\/\/)\w+(\.\w+)*(:[0-9]+)?(.*)$/);
};

export const toPath = (link: string): string => {
  if (!isAbsoluteURL(link)) return withEnvRoot(link);

  const url = new URL(link);
  return withEnvRoot(url.pathname);
};

export const withParams = (link: string, params: { [key: string]: string }): string => {
  const paramString = Object.entries(params)
    .map((param) => { return param[1] ? `${[param[0]]}=${param[1]}` : null; })
    .filter(v => v !== null)
    .join('&');
  if (paramString) {
    return `${link}?${paramString}`;
  }
  return link;
};

export const withRef = (link: string, ref: string): string =>
  withParams(link, { ref });

declare global {
  interface Window {
    ReactQueryClient: QueryClient;
  }
}

export const getOrCreateQueryClient = (): QueryClient => {
  if (!window.ReactQueryClient) {
    window.ReactQueryClient = new QueryClient();
  }
  return window.ReactQueryClient;
};

export const transformKeys = (params: Record<string, unknown>, excludedKeys?: string[]) => {
  const transformFn = deepMapKeys((key: string) =>
    (excludedKeys?.includes(key) ? key : snakeCase(key)));
  return transformFn(params);
};

export const dirtyFormData = (
  dirtyFields: Record<string, unknown> | boolean,
  allValues: Record<string, unknown>
): Record<string, unknown> => {
  if (dirtyFields === true || Array.isArray(dirtyFields)) {
    return allValues;
  }

  if (dirtyFields === false) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(dirtyFields)
      .map(
        ([key, value]) => [
          key,
          dirtyFormData(
            value as Record<string, unknown>,
            allValues[key] as Record<string, unknown>,
          )
        ]
      )
      .filter(
        ([_key, value]) => (isPlainObject(value) ? !isEmpty(value) : true)
      )
  );
};
